import { HttpClient } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { Observable, tap } from 'rxjs';

import { environment } from '../../../environments/environment';

export interface LabeledValue {
  value: string;
  label: string;
}

export type AcServiceListItem = LabeledValue;

export interface CountryOption {
  code: string;
  name: string;
  subdivision_label: string | null;
}

export interface SubdivisionOption {
  code: string;
  name: string;
}

@Injectable({
  providedIn: 'root'
})
export class PicklistService {
  private readonly http = inject(HttpClient);
  private evChargerNetworkTypes = signal<string[]>([]);
  private evChargerNetworkStatuses = signal<string[]>([]);
  private timeZones = signal<string[]>([]);
  private acServices = signal<LabeledValue[]>([]);
  private incentiveAssetClasses = signal<LabeledValue[]>([]);
  private base_url = `${environment.apiUrl}/api/v1/lists/`;

  getEVChargerNetworkTypes() {
    return this.evChargerNetworkTypes.asReadonly();
  }

  getEVChargerNetworkStatuses() {
    return this.evChargerNetworkStatuses.asReadonly();
  }

  getTimeZones() {
    return this.timeZones.asReadonly();
  }

  getAcServices() {
    return this.acServices.asReadonly();
  }

  getIncentiveAssetClasses() {
    return this.incentiveAssetClasses.asReadonly();
  }

  loadCountries(): Observable<CountryOption[]> {
    return this.http.get<CountryOption[]>(this.base_url + 'countries');
  }

  loadSubdivisions(country: string): Observable<SubdivisionOption[]> {
    const code = encodeURIComponent(country);
    return this.http.get<SubdivisionOption[]>(
      this.base_url + `countries/${code}/subdivisions`
    );
  }

  loadTimeZonesForCountry(country: string): Observable<string[]> {
    const code = encodeURIComponent(country);
    return this.http.get<string[]>(this.base_url + `time-zones?country=${code}`);
  }

  loadIncentiveAssetClasses(): Observable<LabeledValue[]> {
    const url = this.base_url + `incentive-asset-classes`;
    return this.http.get<LabeledValue[]>(url).pipe(
      tap((items: LabeledValue[]) => this.incentiveAssetClasses.set(items))
    );
  }

  loadEVChargerNetworkTypes(): Observable<string[]> {
    const url = this.base_url + `ev-charger-network-types`;
    return this.http.get<string[]>(url).pipe(
      tap((types: string[]) => this.evChargerNetworkTypes.set(types))
    );
  }

  loadEVChargerNetworkStatuses(): Observable<string[]> {
    const url = this.base_url + `ev-charger-network-statuses`;
    return this.http.get<string[]>(url).pipe(
      tap((statuses: string[]) => this.evChargerNetworkStatuses.set(statuses))
    );
  }

  loadTimeZones(): Observable<string[]> {
    const url = this.base_url + `time-zones`;
    return this.http.get<string[]>(url).pipe(
      tap((timeZones: string[]) => this.timeZones.set(timeZones))
    );
  }

  loadAcServices(): Observable<LabeledValue[]> {
    const url = this.base_url + `ac-services`;
    return this.http.get<LabeledValue[]>(url).pipe(
      tap((items: LabeledValue[]) => this.acServices.set(items))
    );
  }
}
